import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth, authenticatedFetch } from '../hooks/useAuth';
import { useI18n } from '../i18n/index.jsx';
import { useTutorial } from '../context/TutorialContext';
import ReactGA from 'react-ga4';

// Central API base URL (Lambda)
const API_BASE = 'https://lrv7zfe6dc7hrpucp3qulxlzaq0fszlw.lambda-url.eu-west-3.on.aws'

export default function Dashboard() {
  // Track page view
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: "/dashboard", title: "Dashboard" });
  }, []);

  // Helper: robust userId retrieval (mirrors extension logic)
  function getUserId() {
    if (user?.id) return user.id.toString().trim();
    if (localStorage.getItem('user_id')) return localStorage.getItem('user_id').toString().trim();
    if (sessionStorage.getItem('user_id')) return sessionStorage.getItem('user_id').toString().trim();
    if (window.userId) return window.userId.toString().trim();
    return '';
  }
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const { isActive, setIsActive, currentStep, setCurrentStep } = useTutorial();
  
  // États pour les données du dashboard
  const [storageUsed] = useState(7.5); // legacy (unused for vectors)
  const [storageTotal] = useState(10); // legacy (unused for vectors)
  const [requestsUsed, setRequestsUsed] = useState(0);
  const [requestsQuota, setRequestsQuota] = useState(100);
  const [vectorQuota, setVectorQuota] = useState(500);
  const [totalVectors, setTotalVectors] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);
  
  // États pour les permissions et erreurs
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveOk, setSaveOk] = useState(false);
  
  // États pour les modèles d'IA
  const [aiModels, setAiModels] = useState({
    chatgpt: false,
    gemini: false,
    claude: false,
    grok: false,
    mistral: false,
    deepseek: false,
    perplexity: false
  });
  const [usedLLMs, setUsedLLMs] = useState(''); // Liste des LLMs utilisés séparés par virgule
  const [selectedLLM, setSelectedLLM] = useState(''); // LLM actuellement sélectionné
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showNudgeModal, setShowNudgeModal] = useState(false);
  const [memories, setMemories] = useState([]);
  const [memLoading, setMemLoading] = useState(false);
  const [memError, setMemError] = useState(null);

  // Helper: format relatif simple (fr)
  function timeAgo(dateInput) {
    try {
      const d = new Date(dateInput);
      const diff = Math.max(0, Date.now() - d.getTime());
      const s = Math.floor(diff / 1000);
    if (s < 60) return `${s}s`;
      const m = Math.floor(s / 60);
    if (m < 60) return `${m} min`;
      const h = Math.floor(m / 60);
    if (h < 24) return `${h} h`;
      const dys = Math.floor(h / 24);
    if (dys < 7) return `${dys} d`;
    // otherwise, locale short
      return d.toLocaleString();
    } catch {
      return String(dateInput || '');
    }
  }

  async function fetchMemories() {
    try {
      setMemLoading(true);
      setMemError(null);
  const res = await authenticatedFetch('https://lrv7zfe6dc7hrpucp3qulxlzaq0fszlw.lambda-url.eu-west-3.on.aws/memory/all', { method: 'GET' });
      if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
      const data = await res.json();
      // Supporte plusieurs formes: tableau direct, {memories: []}, {data: []}
      const raw = Array.isArray(data) ? data : (Array.isArray(data?.memories) ? data.memories : (Array.isArray(data?.data) ? data.data : []));
      const mapped = raw.map((it, idx) => ({
        id: it.id_memory ?? it.id ?? idx,
        type: (it.category || it.type || 'Mémoire').toString(),
        content: it.phrase || it.content || '',
        timestamp: timeAgo(it.created_at || it.createdAt || it.timestamp)
      }));
      setMemories(mapped);
    } catch (e) {
      console.error('Erreur chargement mémoires:', e);
      setMemError(e.message || 'Erreur lors du chargement');
    } finally {
      setMemLoading(false);
    }
  }

  // Stats mémoire: weekly_requests, total_vectors
  async function fetchStats() {
    try {
      setStatsLoading(true);
      setStatsError(null);
  const res = await authenticatedFetch('https://lrv7zfe6dc7hrpucp3qulxlzaq0fszlw.lambda-url.eu-west-3.on.aws/memory/stats', { method: 'GET' });
      if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
      const data = await res.json();
  if (typeof data?.weekly_requests === 'number') setRequestsUsed(data.weekly_requests * 3);
      if (typeof data?.total_vectors === 'number') setTotalVectors(data.total_vectors);
    } catch (e) {
      console.error('Erreur stats mémoire:', e);
      setStatsError(e.message || 'Erreur lors du chargement des statistiques');
    } finally {
      setStatsLoading(false);
    }
  }

  // Vérifier si l'utilisateur doit voir le tutoriel
  async function checkOnboardingStatus() {
    // Ne pas afficher le tutoriel si déjà complété
    if (userProfile && userProfile.tuto === true) {
      setIsActive(false)
      return
    }
    // Afficher le tutoriel sinon
    setIsActive(true)
    setCurrentStep(0)
  }

  // Récupérer le profil utilisateur pour déterminer les limites d'abonnement
  async function fetchUserProfile() {
    try {
      setProfileLoading(true);
      setProfileError(null);
      
  const response = await authenticatedFetch('https://lrv7zfe6dc7hrpucp3qulxlzaq0fszlw.lambda-url.eu-west-3.on.aws/auth/profile', {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${response.statusText}`);
      }

      const profile = await response.json();
      
      console.log('📊 Profile complet reçu:', profile);
      console.log('📅 Subscription data:', profile.subscription);
      console.log('🔍 current_period_end:', profile.subscription?.current_period_end);
      console.log('📋 plan:', profile.subscription?.plan);
      
      // Vérifier si l'abonnement est expiré
      let isSubscriptionExpired = false;
      if (profile.subscription?.current_period_end) {
        const expiryDate = new Date(profile.subscription.current_period_end);
        const now = new Date();
        console.log('⏰ Date d\'expiration:', expiryDate.toISOString());
        console.log('⏰ Date actuelle:', now.toISOString());
        console.log('⏰ Abonnement expiré?', now > expiryDate);
        
        if (now > expiryDate) {
          isSubscriptionExpired = true;
          // Marquer l'abonnement comme expiré en supprimant le plan
          profile.subscription.plan = null;
          console.warn('❌ Abonnement expiré depuis:', expiryDate);
        } else {
          console.log('✅ Abonnement actif jusqu\'au:', expiryDate);
        }
      } else {
        console.log('⚠️ Aucune date d\'expiration trouvée (date_fin ou current_period_end)');
      }
      
      setUserProfile(profile);
      
      // Déterminer les limites selon le plan d'abonnement
      const plan = profile.subscription?.plan;

      // IDs Pro (test + live)
      const proPlans = [
        'price_1SJwWdGbM2UDa2ApELcU7uNg', // pro monthly test
        'price_1SJwXGGbM2UDa2ApvW4Z8d0M', // pro yearly test
        'price_1SJvHrGbM2UDa2AprRHQfoKo', // pro monthly live
        'price_1SJvIgGbM2UDa2ApJyavjIUo'  // pro yearly live
      ];
      // IDs Max (test + live)
      const maxPlans = [
        'price_1SJwWxGbM2UDa2ApR7krPBg2', // max monthly test
        'price_1SJwXYGbM2UDa2ApdNuuWXzg', // max yearly test
        'price_1SJvJRGbM2UDa2Apayvg7rz7', // max monthly live
        'price_1SJvK6GbM2UDa2ApNmkEeIt1'  // max yearly live
      ];

      if (maxPlans.includes(plan)) {
        // Plan Max
        setVectorQuota(500000);
        setRequestsQuota(30000);
      } else if (proPlans.includes(plan)) {
        // Plan Pro
        setVectorQuota(50000);
        setRequestsQuota(2500);
      } else {
        // Plan Free (aucun abonnement ou abonnement expiré/non renouvelé)
        setVectorQuota(10000);
        setRequestsQuota(500);
      }
    } catch (e) {
      console.error('Erreur profil utilisateur:', e);
      setProfileError(e.message || 'Erreur lors du chargement du profil');
      // Valeurs par défaut en cas d'erreur
      setVectorQuota(500);
      setRequestsQuota(100);
    } finally {
      setProfileLoading(false);
    }
  }

  const toggleAiModel = (key) => {
    // Vérifier si l'utilisateur a les permissions pour ce modèle
    const plan = userProfile?.subscription?.plan;
    const proPlans = [
        'price_1SJwWdGbM2UDa2ApELcU7uNg', // pro monthly test
        'price_1SJwXGGbM2UDa2ApvW4Z8d0M', // pro yearly test
        'price_1SJvHrGbM2UDa2AprRHQfoKo', // pro monthly live
        'price_1SJvIgGbM2UDa2ApJyavjIUo'  // pro yearly live
      ];
    const maxPlans = [
        'price_1SJwWxGbM2UDa2ApR7krPBg2', // max monthly test
        'price_1SJwXYGbM2UDa2ApdNuuWXzg', // max yearly test
        'price_1SJvJRGbM2UDa2Apayvg7rz7', // max monthly live
        'price_1SJvK6GbM2UDa2ApNmkEeIt1'  // max yearly live
      ];
    const isPaidPlan = proPlans.includes(plan) || maxPlans.includes(plan);
    const premiumModels = ['grok', 'mistral', 'deepseek'];

    setAiModels(prev => {
      const nextVal = !prev[key];
      // Pour Pro et Max, aucune restriction sur les modèles premium
      if (!isPaidPlan && premiumModels.includes(key) && nextVal === true) {
        setShowUpgradeModal(true);
        return prev;
      }
      // Pour Pro et Max, on modifie librement tous les modèles
      return { ...prev, [key]: nextVal };
    });
  };

  // Fonction pour charger les LLMs utilisés
  async function fetchUsedLLMs() {
    try {
      const token = (localStorage.getItem('access_token') || '').trim();
      const res = await fetch(`${API_BASE}/llms/used`, {
        method: 'GET',
        headers: {
          'Authorization': token ? `Bearer ${token}` : undefined
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        const llmsList = data?.used_llms || data?.llms || '';
        setUsedLLMs(llmsList);
      }
    } catch (e) {
      console.warn('Erreur chargement LLMs utilisés:', e);
    }
  }

  // Fonction pour sauvegarder le LLM sélectionné et mettre à jour la liste
  async function saveLLMSelection() {
    try {
      if (!selectedLLM) return;
      
      setSaving(true);
      const token = (localStorage.getItem('access_token') || '').trim();
      
      // Ajouter à la liste des LLMs utilisés si pas déjà présent
      let updatedList = usedLLMs;
      const llmArray = usedLLMs.split(',').map(l => l.trim()).filter(l => l);
      const modelLabels = {
        chatgpt: 'ChatGPT',
        gemini: 'Gemini',
        grok: 'Grok',
        mistral: 'Mistral',
        deepseek: 'DeepSeek',
        perplexity: 'Perplexity AI'
      };
      
      const llmName = modelLabels[selectedLLM] || selectedLLM;
      if (!llmArray.includes(llmName)) {
        llmArray.push(llmName);
        updatedList = llmArray.join(', ');
      }
      
      // Sauvegarder sur le serveur
      const res = await fetch(`${API_BASE}/llms/update`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : undefined,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          selected_llm: selectedLLM,
          used_llms: updatedList
        })
      });
      
      if (res.ok) {
        setUsedLLMs(updatedList);
        setSaveOk(true);
        setTimeout(() => setSaveOk(false), 2500);
      } else {
        setSaveError('Erreur lors de la sauvegarde du LLM');
      }
    } catch (e) {
      console.error('Erreur sauvegarde LLM:', e);
      setSaveError(e.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  }

  // Fonction pour récupérer les permissions utilisateur
  async function fetchUserInfo() {
    try {
      setLoading(true);
      setError(null);
      const token = (localStorage.getItem('access_token') || '').trim();
      let data = null;
      try {
        const res1 = await fetch(`${API_BASE}/perms/get`, {
          method: 'GET',
          headers: {
            'Authorization': token ? `Bearer ${token}` : undefined
          }
        });
        if (res1.ok) {
          data = await res1.json();
        } else {
          throw new Error(`Proxy GET failed ${res1.status}`);
        }
      } catch (e) {
        console.warn('Proxy perms GET failed, trying lambda URL:', e?.message);
        const res2 = await fetch(`${API_BASE}/perms/get`, {
          method: 'GET',
          headers: {
            'Authorization': token ? `Bearer ${token}` : undefined
          }
        });
        if (!res2.ok) throw new Error(`HTTP ${res2.status} ${res2.statusText}`);
        data = await res2.json();
      }

      const perms = data?.permissions || data || {};
      setPermissions(perms);

      // Mapper les permissions serveur -> UI (Perplexity toujours false car "Bientôt")
      setAiModels({
        chatgpt: !!perms.chatgpt,
        gemini: !!perms.gemini,
        claude: !!perms.claude,
        grok: !!perms.grok,
        mistral: !!perms.mistral,
        deepseek: !!perms.deepseek,
        perplexity: false
      });
    } catch (err) {
      console.error('Erreur de connexion:', err);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  }

  // Mettre à jour les permissions utilisateur
  async function savePermissions() {
    try {
      setSaving(true);
      setSaveError(null);
      setSaveOk(false);

      const token = (localStorage.getItem('access_token') || '').trim();
      // Corps attendu par l'API
      const body = JSON.stringify({
        chatgpt: !!aiModels.chatgpt,
        gemini: !!aiModels.gemini,
        claude: !!aiModels.claude,
        grok: !!aiModels.grok,
        mistral: !!aiModels.mistral,
        deepseek: !!aiModels.deepseek
        // Perplexity non envoyé (Bientôt)
      });

      let ok = false;
      try {
        const res1 = await fetch(`${API_BASE}/perms/update`, {
          method: 'POST',
          headers: {
            'Authorization': token ? `Bearer ${token}` : undefined,
            'Content-Type': 'application/json'
          },
          body
        });
        ok = res1.ok;
        if (!ok) throw new Error(`Proxy POST failed ${res1.status}`);
      } catch (e) {
        console.warn('Proxy perms POST failed, trying lambda URL:', e?.message);
        const res2 = await fetch(`${API_BASE}/perms/update`, {
          method: 'POST',
          headers: {
            'Authorization': token ? `Bearer ${token}` : undefined,
            'Content-Type': 'application/json'
          },
          body
        });
        if (!res2.ok) throw new Error(`HTTP ${res2.status} ${res2.statusText}`);
        ok = true;
      }

      // Optionnel: recharger depuis le serveur pour rester source de vérité
      await fetchUserInfo();
      setSaveOk(true);
      // Masquer le succès après un court délai
      setTimeout(() => setSaveOk(false), 2500);
    } catch (e) {
      console.error('Erreur de sauvegarde des permissions:', e);
      setSaveError(e.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  }

  // Charger les permissions au montage du composant
  useEffect(() => {
    if (user) {
      fetchUserProfile(); // Charger le profil d'abord pour déterminer les limites ET l'onboarding
      fetchUserInfo();
      fetchMemories();
      fetchStats();
      fetchUsedLLMs(); // Charger les LLMs utilisés
    }
  }, [user]);

  // Vérifier le statut du tutoriel quand le profil utilisateur est chargé
  useEffect(() => {
    if (userProfile) {
      checkOnboardingStatus();
    }
  }, [userProfile]);

  // 🔥 NOUVEAU: Recharger le profil quand le tutoriel se termine
  useEffect(() => {
    if (!isActive && user) {
      // Le tutoriel vient de se terminer, recharger le profil
      console.log('🔄 Tutoriel terminé, rechargement du profil...');
      fetchUserProfile();
    }
  }, [isActive]);

  const vectorUsed = totalVectors ?? 0;
  const storagePercentage = Math.min(100, (vectorUsed / vectorQuota) * 100);
  const requestsPercentage = Math.min(100, (requestsUsed / requestsQuota) * 100);

  // Ouvrir un pop-up quand la limite du plan est atteinte (requêtes ou vecteurs)
  useEffect(() => {
    if (!Number.isFinite(requestsQuota) || !Number.isFinite(vectorQuota)) return;
    const requestsAtLimit = requestsQuota > 0 && requestsUsed >= requestsQuota;
    const vectorsAtLimit = vectorQuota > 0 && vectorUsed >= vectorQuota;
    if (requestsAtLimit || vectorsAtLimit) {
      setShowLimitModal(true);
    }
    // pas de else -> on ne force pas la fermeture automatiquement
  }, [requestsUsed, requestsQuota, vectorUsed, vectorQuota]);

  // Nudge modal: prévenir avant d'atteindre la limite (max ~3/jour, avec proba)
  useEffect(() => {
    // Pré-requis: profil chargé et pas déjà un autre modal
    if (!userProfile || showLimitModal || showUpgradeModal) return;

    const plan = userProfile?.subscription?.plan;
    const isPaidPlan = plan && plan !== 'free';
    if (isPaidPlan) return; // seulement pour plan gratuit

    if (!Number.isFinite(requestsQuota) || !Number.isFinite(vectorQuota)) return;
    const nearLimit = (requestsQuota > 0 && requestsUsed / requestsQuota >= 0.8) ||
                      (vectorQuota > 0 && vectorUsed / vectorQuota >= 0.8);

    // Gestion de la fréquence quotidienne
    const today = new Date();
    const ymd = `${today.getFullYear()}-${today.getMonth()+1}-${today.getDate()}`;
    const last = localStorage.getItem('nudge_last_date') || '';
    let count = Number(localStorage.getItem('nudge_count') || '0');
    if (last !== ymd) {
      // reset quotidien
      localStorage.setItem('nudge_last_date', ymd);
      count = 0;
      localStorage.setItem('nudge_count', '0');
    }
    if (count >= 3) return; // max ~3/jour

    // Probabilité de déclenchement si pas proche de la limite
    const probability = nearLimit ? 1 : 0.25; // 100% si >=80%, sinon 25%
    if (Math.random() <= probability) {
      setShowNudgeModal(true);
      const next = count + 1;
      localStorage.setItem('nudge_count', String(next));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile, requestsUsed, requestsQuota, vectorUsed, vectorQuota]);

  return (
    <DashboardLayout>
      {/* Glows décoratifs - cachés sur mobile */}
      <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-emerald-500/25 blur-3xl hidden md:block" />
      <div className="pointer-events-none absolute bottom-[-90px] right-[-90px] h-96 w-96 rounded-full bg-sky-500/20 blur-3xl hidden md:block" />

      <div className="relative z-10 py-6 md:py-12 lg:py-20 px-4 md:px-6">
        <div className="mx-auto max-w-7xl">
            {/* Header */}
            <div className="mb-6 md:mb-12 text-center">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight sm:text-5xl">
                {t('nav.dashboard')}
                {userProfile?.subscription && [
                  'price_1SJwWdGbM2UDa2ApELcU7uNg', // pro monthly test
                  'price_1SJwXGGbM2UDa2ApvW4Z8d0M', // pro yearly test
                  'price_1SJvHrGbM2UDa2AprRHQfoKo', // pro monthly live
                  'price_1SJvIgGbM2UDa2ApJyavjIUo'  // pro yearly live
                ].includes(userProfile.subscription.plan) && (
                  <span className="ml-2 px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-indigo-500 to-purple-500 text-white" style={{
                    fontWeight: 700,
                    fontSize: '1rem',
                    letterSpacing: '0.5px',
                    boxShadow: '0 2px 8px rgba(128,90,213,0.10)',
                    border: '2px solid #8b5cf6',
                    textShadow: '0 1px 4px #fff8',
                    display: 'inline-block',
                    verticalAlign: 'middle'
                  }}>pro</span>
                )}
                {userProfile?.subscription && [
                  'price_1SJwWxGbM2UDa2ApR7krPBg2', // max monthly test
                  'price_1SJwXYGbM2UDa2ApdNuuWXzg', // max yearly test
                  'price_1SJvJRGbM2UDa2Apayvg7rz7', // max monthly live
                  'price_1SJvK6GbM2UDa2ApNmkEeIt1'  // max yearly live
                ].includes(userProfile.subscription.plan) && (
                  <span className="ml-2 px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 text-yellow-900">max</span>
                )}
              </h1>
              <p className="mt-2 md:mt-3 text-sm md:text-base text-slate-400">{t('dashboard.subtitle')}</p>
            </div>

            {/* Grid Layout - Responsive */}
            <div className="grid gap-4 md:gap-8 grid-cols-1 lg:grid-cols-2">
              
              {/* 1. Espace de stockage avec barre de progression */}
              <div id="storage-section" className="rounded-2xl border border-white/10 bg-white/5 p-4 md:p-8 backdrop-blur-2xl flex flex-col justify-between h-full">
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <h3 className="text-lg md:text-2xl font-semibold">{t('dashboard.storage')}</h3>
                  <div className="rounded-full bg-emerald-500/20 p-2 md:p-3">
                    <svg className="w-5 h-5 md:w-6 md:h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2h4a1 1 0 011 1v16a1 1 0 01-1 1H3a1 1 0 01-1-1V5a1 1 0 011-1h4z" />
                    </svg>
                  </div>
                </div>
                
                <div className="space-y-3 md:space-y-4">
                  <div className="flex justify-between text-xs md:text-sm">
                    <span className="text-slate-300">{t('dashboard.vectorsUsed')}</span>
                    <span className="text-white font-medium">{vectorUsed.toLocaleString()} / {vectorQuota.toLocaleString()}</span>
                  </div>
                  
                  <div className="w-full bg-slate-700/50 rounded-full h-2 md:h-3">
                    <div 
                      className="bg-gradient-to-r from-emerald-500 to-emerald-400 h-2 md:h-3 rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${storagePercentage}%` }}
                    />
                  </div>
                  
                  <div className="text-xs text-slate-400">
                    {t('dashboard.spaceFree', { percent: (Math.max(0, 100 - storagePercentage)).toFixed(1) })}
                  </div>
                </div>
              </div>

              {/* 2. Requêtes mémoire */}
              <div id="requests-section" className="rounded-2xl border border-white/10 bg-white/5 p-4 md:p-8 backdrop-blur-2xl">
                <div className="flex items-center justify-between mb-4 md:mb-6">
          <h3 className="text-lg md:text-2xl font-semibold">{t('dashboard.memoryRequests')}</h3>
                  <div className="rounded-full bg-sky-500/20 p-2 md:p-3">
                    <svg className="w-5 h-5 md:w-6 md:h-6 text-sky-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                </div>
                
                <div className="space-y-3 md:space-y-4">
                  <div className="flex justify-between items-end">
                    <div>
                      <div className="text-2xl md:text-3xl font-bold text-white">{requestsUsed.toLocaleString()}</div>
            <div className="text-xs md:text-sm text-slate-400">{t('dashboard.requestsOfQuota', { quota: requestsQuota.toLocaleString() })}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs md:text-sm font-medium text-sky-400">{requestsPercentage.toFixed(1)}%</div>
            <div className="text-[10px] md:text-xs text-slate-400">{t('dashboard.used')}</div>
                    </div>
                  </div>
                  
                  <div className="w-full bg-slate-700/50 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-sky-500 to-sky-400 h-2 rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${requestsPercentage}%` }}
                    />
                  </div>
                  
                  <div className="text-xs text-slate-400">
                    {t('dashboard.requestsRemaining', { count: Math.max(0, requestsQuota - requestsUsed) })}
                  </div>
                  {statsError && (
                    <div className="text-xs text-red-300">{statsError}</div>
                  )}
                  {profileError && (
                    <div className="text-xs text-orange-300">
                      Profil: {profileError}
                    </div>
                  )}
                  {profileLoading && (
                    <div className="flex items-center text-xs text-slate-400">
                      <div className="animate-spin rounded-full h-3 w-3 border-b border-slate-400 mr-2"></div>
                      Chargement du profil...
                    </div>
                  )}
                </div>
              </div>

              {/* 3. Historique des entrées mémoire */}
              <div id="history-section" className="rounded-2xl border border-white/10 bg-white/5 p-4 md:p-8 backdrop-blur-2xl flex flex-col">
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <h3 className="text-lg md:text-2xl font-semibold">{t('dashboard.memoryHistory')}</h3>
                  <div className="rounded-full bg-purple-500/20 p-2 md:p-3">
                    <svg className="w-5 h-5 md:w-6 md:h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                </div>
                {/* Etat de chargement / erreur */}
                {memLoading && (
                  <div className="flex items-center justify-center py-6">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-400"></div>
                    <span className="ml-3 text-slate-400 text-sm">{t('dashboard.loadingMemories')}</span>
                  </div>
                )}

                {memError && (
                  <div className="mb-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-red-300">{memError}</span>
                      <button onClick={fetchMemories} className="text-xs text-red-200 underline">{t('common.retry')}</button>
                    </div>
                  </div>
                )}

                {!memLoading && !memError && (
                  <div className="space-y-2 md:space-y-3 max-h-[520px] overflow-y-auto custom-scrollbar">
                    {memories.length === 0 ? (
                      <div className="text-xs md:text-sm text-slate-400 py-2">{t('dashboard.noMemories')}</div>
                    ) : (
                      memories.map((entry) => (
                        <div key={entry.id} className="border-l-3 border-purple-400/50 pl-4 py-2">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-purple-400 uppercase tracking-wide">
                              {entry.type}
                            </span>
                            <span className="text-xs text-slate-500">{entry.timestamp}</span>
                          </div>
                          <p className="text-sm text-slate-300 line-clamp-2">
                            {entry.content}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                )}
                
                <button
                  onClick={() => navigate('/souvenirs')}
                  className="mt-4 w-full rounded-lg border border-white/10 bg-white/5 py-2 text-sm text-slate-300 hover:bg-white/10 transition-colors"
                >
                  {t('dashboard.viewAllHistory')}
                </button>
              </div>

              {/* 4. Gestion des droits d'extension */}
              <div id="permissions-section" className="rounded-2xl border border-white/10 bg-white/5 p-4 md:p-8 backdrop-blur-2xl">
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <h3 className="text-lg md:text-2xl font-semibold">{t('dashboard.aiPermissions')}</h3>
                  <div className="rounded-full bg-amber-500/20 p-2 md:p-3">
                    <svg className="w-5 h-5 md:w-6 md:h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                </div>

                {/* État de chargement */}
                {loading && (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400"></div>
                    <span className="ml-3 text-slate-400">{t('dashboard.loadingPermissions')}</span>
                  </div>
                )}

                {/* Gestion des erreurs */}
                {error && (
                  <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <span className="text-red-400 text-sm">{error}</span>
                      </div>
                      <button 
                        onClick={fetchUserInfo}
                        className="text-xs text-red-300 hover:text-red-200 underline"
                      >
                        Réessayer
                      </button>
                    </div>
                  </div>
                )}

                {/* Feedback sauvegarde */}
                {(saveError || saveOk) && (
                  <div className={`mb-4 p-3 rounded-lg border ${saveOk ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/20'}`}>
                    <span className={`text-xs ${saveOk ? 'text-emerald-300' : 'text-red-300'}`}>
                      {saveOk ? t('dashboard.permissionsSaved') : `${t('dashboard.saveErrorPrefix')} ${saveError}`}
                    </span>
                  </div>
                )}

                {/* Modèles d'IA */}
                {!loading && (
                  <div className="space-y-3 md:space-y-4">
                    {Object.entries(aiModels).map(([key, enabled]) => {
                      const modelLabels = {
                        chatgpt: 'ChatGPT',
                        gemini: 'Gemini',
                        claude: 'Claude',
                        grok: 'Grok',
                        mistral: 'Mistral',
                        deepseek: 'DeepSeek',
                        perplexity: 'Perplexity AI'
                      };
                      const comingSoon = new Set(['perplexity']);
                      const isSoon = comingSoon.has(key);
                      const shownEnabled = isSoon ? false : enabled;

                      // Nouvelle logique : aucune restriction ni badge "Pro" pour Pro/Max
                      const plan = userProfile?.subscription?.plan;
                      const proPlans = [
                        'price_1SJwWdGbM2UDa2ApELcU7uNg', // pro monthly test
                        'price_1SJwXGGbM2UDa2ApvW4Z8d0M', // pro yearly test
                        'price_1SJvHrGbM2UDa2AprRHQfoKo', // pro monthly live
                        'price_1SJvIgGbM2UDa2ApJyavjIUo'  // pro yearly live
                      ];
                      const maxPlans = [
                        'price_1SJwWxGbM2UDa2ApR7krPBg2', // max monthly test
                        'price_1SJwXYGbM2UDa2ApdNuuWXzg', // max yearly test
                        'price_1SJvJRGbM2UDa2Apayvg7rz7', // max monthly live
                        'price_1SJvK6GbM2UDa2ApNmkEeIt1'  // max yearly live
                      ];
                      const isPaidPlan = proPlans.includes(plan) || maxPlans.includes(plan);
                      const premiumModels = ['claude', 'grok', 'mistral', 'deepseek'];
                      const isPremium = premiumModels.includes(key);
                      const needsUpgrade = isPremium && !isPaidPlan;

                      return (
                        <div key={key} className="flex items-center justify-between py-2 md:py-3">
                          <div className="flex items-center space-x-2 md:space-x-3">
                            <div className={`w-2.5 h-2.5 md:w-3 md:h-3 rounded-full transition-colors ${
                              needsUpgrade ? 'bg-amber-500' : shownEnabled ? 'bg-green-500' : 'bg-red-500'
                            }`} />
                            <span className="text-sm md:text-base text-slate-300 flex items-center gap-1.5 md:gap-2">
                              {modelLabels[key]}
                              {isSoon && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-slate-300">{t('common.soon')}</span>
                              )}
                              {/* Affiche le badge "Pro" uniquement pour les plans gratuits */}
                              {!isPaidPlan && needsUpgrade && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-300">Pro</span>
                              )}
                            </span>
                          </div>
                          <button
                            onClick={() => toggleAiModel(key)}
                            disabled={loading || isSoon}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 disabled:opacity-50 ${
                              needsUpgrade ? 'bg-amber-500 focus:ring-amber-500 cursor-pointer' :
                              shownEnabled 
                                ? 'bg-green-500 focus:ring-green-500' 
                                : 'bg-red-500 focus:ring-red-500'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                needsUpgrade ? 'translate-x-1' :
                                shownEnabled ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                <div className="mt-6 md:mt-8 pt-4 md:pt-6 border-t border-white/10 flex gap-2 md:gap-3">
                  <button 
                    onClick={fetchUserInfo}
                    disabled={loading || saving}
                    className="flex-1 rounded-lg bg-slate-700/60 py-2 md:py-3 text-xs md:text-sm font-medium text-slate-200 hover:bg-slate-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? t('common.loading') : t('common.refresh')}
                  </button>
                  <button 
                    onClick={savePermissions}
                    disabled={saving || loading}
                    className="flex-1 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-400 py-2 md:py-3 text-xs md:text-sm font-medium text-white hover:from-emerald-600 hover:to-emerald-500 transition-all shadow-lg hover:shadow-emerald-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? t('actions.saving') : t('actions.save')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

      {/* Modal d'upgrade pour modèles IA premium */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="mx-auto max-w-md w-full rounded-2xl border border-white/20 bg-slate-900/95 p-4 md:p-6 shadow-2xl">
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r from-amber-500 to-orange-500">
                <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              
              <h3 className="text-base md:text-lg font-semibold text-white mb-2">
                Modèle IA Premium
              </h3>
              
              <p className="text-xs md:text-sm text-slate-300 mb-4 md:mb-6">
                Grok, Mistral et DeepSeek sont des modèles IA premium. Passez à un plan payant pour y accéder !
              </p>
              
              <div className="space-y-2 md:space-y-3 mb-4 md:mb-6">
                <div className="flex items-center justify-between text-xs md:text-sm">
                  <span className="text-slate-400">✅ ChatGPT & Gemini</span>
                  <span className="text-green-400 font-medium">Gratuit</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">🚀 Grok, Mistral, DeepSeek</span>
                  <span className="text-amber-400 font-medium">Pro/Max</span>
                </div>
              </div>
              
              <div className="flex gap-2 md:gap-3">
                <button 
                  onClick={() => setShowUpgradeModal(false)}
                  className="flex-1 rounded-lg border border-slate-600 px-3 md:px-4 py-2 text-xs md:text-sm text-slate-300 hover:bg-slate-800"
                >
                  Fermer
                </button>
                <button 
                  onClick={() => {
                    window.open('https://billing.stripe.com/p/login/test_8wM7sU6C8dLN5Bm000', '_blank');
                    setShowUpgradeModal(false);
                  }}
                  className="flex-1 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 px-3 md:px-4 py-2 text-xs md:text-sm font-medium text-white hover:from-amber-600 hover:to-orange-600"
                >
                  Passer au Pro
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: limites atteintes (requêtes/vecteurs) */}
      {showLimitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="mx-auto max-w-md w-full rounded-2xl border border-white/20 bg-slate-900/95 p-4 md:p-6 shadow-2xl">
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r from-emerald-500 to-sky-500">
                <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>

              <h3 className="text-lg font-semibold text-white mb-2">
                {t('limits.title')}
              </h3>

              <p className="text-sm text-slate-300 mb-4">
                {t('limits.desc')}
              </p>

              <div className="mb-6 space-y-2 text-sm">
                {requestsQuota > 0 && requestsUsed >= requestsQuota && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">{t('dashboard.memoryRequests')}</span>
                    <span className="text-rose-300 font-medium">
                      {t('limits.reached.requests', { used: requestsUsed.toLocaleString(), quota: requestsQuota.toLocaleString() })}
                    </span>
                  </div>
                )}
                {vectorQuota > 0 && vectorUsed >= vectorQuota && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">{t('dashboard.vectorsUsed')}</span>
                    <span className="text-rose-300 font-medium">
                      {t('limits.reached.vectors', { used: vectorUsed.toLocaleString(), quota: vectorQuota.toLocaleString() })}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex gap-3 flex-wrap justify-stretch">
                {vectorQuota > 0 && vectorUsed >= vectorQuota && (
                  <button 
                    onClick={() => {
                      setShowLimitModal(false);
                      navigate('/souvenirs');
                    }}
                    className="flex-1 rounded-lg border border-emerald-600/40 px-4 py-2 text-sm text-emerald-200 hover:bg-emerald-600/10"
                  >
                    {t('limits.btn.cleanup')}
                  </button>
                )}
                <button 
                  onClick={() => setShowLimitModal(false)}
                  className="flex-1 rounded-lg border border-slate-600 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800"
                >
                  {t('limits.btn.close')}
                </button>
                <button 
                  onClick={() => {
                    setShowLimitModal(false);
                    navigate('/pricing');
                  }}
                  className="flex-1 rounded-lg bg-gradient-to-r from-emerald-500 to-sky-500 px-4 py-2 text-sm font-medium text-white hover:from-emerald-600 hover:to-sky-600"
                >
                  {t('limits.btn.upgrade')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Nudge modal: bientôt à la limite */}
      {showNudgeModal && !showLimitModal && !showUpgradeModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="mx-auto max-w-md w-full rounded-2xl border border-white/20 bg-slate-900/95 p-4 md:p-6 shadow-2xl">
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r from-emerald-500 to-sky-500">
                <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" />
                </svg>
              </div>

              <h3 className="text-lg font-semibold text-white mb-2">{t('nudge.title')}</h3>
              <p className="text-sm text-slate-300 mb-6">{t('nudge.desc')}</p>

              <div className="flex gap-3">
                <button 
                  onClick={() => setShowNudgeModal(false)}
                  className="flex-1 rounded-lg border border-slate-600 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800"
                >
                  {t('nudge.btn.later')}
                </button>
                <button 
                  onClick={() => { setShowNudgeModal(false); navigate('/pricing'); }}
                  className="flex-1 rounded-lg bg-gradient-to-r from-emerald-500 to-sky-500 px-4 py-2 text-sm font-medium text-white hover:from-emerald-600 hover:to-sky-600"
                >
                  {t('nudge.btn.plans')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
